"use client";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import i18n from "i18next";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePublicProfileStore } from "@/features/profile/public/store/usePublicProfileStore";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Import availableLanguages like in SettingsCard
import availableLanguages from "@/i18n/availableLanguages";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";

interface props {
  isInReviews?: boolean;
}

export function PublicLanguageSwitcher({ isInReviews }: props) {
  const { profileData } = usePublicProfileStore();
  const { profileId, locale: locale_code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("profile", {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });

  if (!profileData || !profileData.available_locales || profileData.available_locales.length <= 1) {
    return null;
  }

  const handleLanguageChange = (locale: string) => {
    navigate(`/profile/tutor${isInReviews ? "/reviews" : ""}/${profileId}/${locale}`);
  };

  const profileLanguages = profileData.available_locales
    .sort()
    .map((locale: string) =>
      availableLanguages.find((lang) => lang.language.toUpperCase() === locale),
    )
    .filter((l) => l !== undefined);

  return (
    <Card className="bg-side-accent/50 relative h-fit w-full overflow-hidden rounded-2xl border-l-4 border-none border-l-indigo-400/50 shadow-2xl backdrop-blur-xl sm:min-w-[23rem] lg:max-w-[35rem] xl:w-fit">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-indigo-400 to-purple-600" />
        <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-purple-400 to-indigo-500" />
      </div>
      <CardContent className="flex max-w-4xl flex-col justify-start gap-0 space-y-6">
        <div className="w-full">
          <CardHeader className="flex flex-row justify-between ps-0 pb-3">
            <CardTitle className="text-accent-foreground flex items-center gap-2 text-xl font-bold">
              <div className="flex items-center justify-center rounded-xl bg-indigo-500/20 p-2">
                <Icon
                  className="text-indigo-400" // Changed from text-muted-foreground
                  height="1.5rem"
                  icon="mdi:internet"
                  width="1.5rem"
                />
              </div>
              {t("settingsSection.profileLanguage")}
            </CardTitle>
          </CardHeader>
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-2"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            <Tabs
              className="w-full"
              defaultValue={locale_code === undefined ? i18n.language.toUpperCase() : locale_code}
              onValueChange={handleLanguageChange}
            >
              <TabsList className="flex w-full gap-2 rounded-xl bg-indigo-500/20 backdrop-blur-sm">
                {profileLanguages.map((lang, index) => (
                  <motion.div
                    key={`${lang.language}-${index}`}
                    animate={{ opacity: 1 }}
                    className="w-full"
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <TabsTrigger
                      className={`flex w-full items-center gap-2 rounded-xl data-[state=active]:bg-indigo-500/30 data-[state=active]:text-indigo-300`}
                      value={lang.language.toUpperCase()}
                    >
                      <img
                        alt={`${lang.name} flag`}
                        className="h-6 w-8 rounded object-cover"
                        src={lang.flagImg || "/placeholder.svg"}
                      />
                    </TabsTrigger>
                  </motion.div>
                ))}
              </TabsList>
            </Tabs>
          </motion.div>
        </div>
      </CardContent>
    </Card>
  );
}
